import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import reportslogo from "../../../assets/images/reportslogo.png";
import { DefaultCard } from "../../../assets/styled-components/card";
import CustomAgGrid from '../../commoncomponents/CustomAgGrid';
import { BemsGraph } from '../../../utility/apiURLS';
import axios from 'axios';
import { getAuthToken, getcompanyId, getFacilityId, getuserId } from '../../../utility/tokenUtility';
import SaveAltRoundedIcon from "@mui/icons-material/SaveAltRounded";
import ExcelJS from "exceljs"; // Changed from 'xlsx' to 'exceljs'
import { saveAs } from "file-saver";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ViewAssetMedicalRiskChecklist from './ViewAssetMedicalRiskChecklist';
import { jsPDF } from "jspdf";
import html2canvas from 'html2canvas';

const AssetMedicalRiskChecklist = forwardRef(({ data }, ref) => {
    const pdfReportRef = useRef(null);
    const [gridApi, setGridApi] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const facilityName = data?.hospital === "" ? "All" : data?.hospital?.facilityName || "";
    const riskNmae = data?.riskStatus.value === "All" ? "High/Moderate" : data?.riskStatus.value === "High Risk" ? "High" : "Moderate";

    const token = getAuthToken("id_token");
    const userId = getuserId("userId");
    const companyId = getcompanyId("companyId");
    const facilityId = getFacilityId("facilityId");

    const columnNames = [
        { field: "hospitalName", headerName: "HOSPITAL NAME" },
        { field: "assetNo", headerName: "ASSET NO." },
        { field: "assetDescription", headerName: "ASSET DESC" },
        {
            field: "assetAge",
            headerName: "Asset Age",
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: "right" },
            filterParams: {
                // 1. Force AG Grid to only show a single filter condition block
                maxNumConditions: 1,

                // 2. Keep 'Between' (inRange) as your primary option
                filterOptions: ['inRange', 'greaterThan', 'lessThan', 'equals'],
                defaultOption: 'inRange',

                // 3. Action buttons
                buttons: ['apply', 'clear', 'reset'],
                closeOnApply: true,

            }
        },
        { field: "serialNo", headerName: "S/N / MODEL" },
        { field: "departmentDescription", headerName: "DEPT DESC" },
        { field: "patientimpactrisk", headerName: "PATIENT IMPACT RISK" },
        { field: "failedsafetyperformance", headerName: "FAILED SAFETY / PERFORMANCE" },
        { field: "hazardrisk", headerName: "HAZARD RISK" },
        { field: "highrepaircost", headerName: "HIGH REPAIR COST" },
        { field: "badphysicalcondition", headerName: "BAD PHYSICAL CONDITION" },
        { field: "breakdownfreq", headerName: "BREAKDOWN FREQ >5 TIMES/YEAR" },
        { field: "assetCondition", headerName: "ASSET CONDITION" },
    ];

    useEffect(() => {
        if (data) {
            fetchchecklist();
        }
    }, [data]);

    const fetchchecklist = async () => {
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = {
                Userid: userId,
                Companyid: companyId,
                HospitalId: data?.hospital === ""
                    ? "00000000-0000-0000-0000-000000000000"
                    : data?.hospital?.facilityId,
                Assetconditionid: data?.assetcondition?.id,
                Quarter: data?.month?.value,
                Risklevelbems: data?.riskStatus?.value,
                Year: data?.year?.value,
                Token: token
            };

            const response = await axios.get(
                `${BemsGraph}/api/AssetReport/GetAssetMedicalRiskChecklist`,
                { params: payload, ...config }
            );

            const options = response.data;
            setRows(options?.bemsAssetChecklists || []);
        } catch (error) {
            console.error("Failed to fetch hospital data:", error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const getBase64FromUrl = async (url) => {
        const data = await fetch(url);
        const blob = await data.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
                const base64data = reader.result;
                // Strip the metadata prefix to give raw base64 to exceljs
                resolve(base64data.split(',')[1]);
            };
        });
    };

    // const handleDownloadPDF = async () => {
    //     if (pdfReportRef.current) {
    //         try {
    //             setLoading(true); // Optional: if you keep track of loading spinners

    //             // Invoke the clean native AutoTable generation routine directly
    //             await pdfReportRef.current.generatePDF();

    //         } catch (err) {
    //             console.error("PDF Native Generation Failure:", err);
    //         } finally {
    //             setLoading(false);
    //         }
    //     }
    // };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Checklist", {
            views: [{ showGridLines: true }]
        });

        // Color Palette Definitions
        const colors = {
            mainBanner: "8B2522",   // Dark Maroon Banner
            subBanner: "F9EBEA",    // Light Rose Tint
            headerMaroon: "78281F", // Main Table Header Dark Red
            headerTeal: "115E59",   // Asset Condition Header Dark Teal
            tickHeader: "D6EAF8",   // Soft Blue rule table header
            zebraColor: "FDF2F2",   // Clean light rose tint for alternate rows
            thinBorder: "BDC3C7"    // Muted grey for grid lines
        };

        const thinBorderConfig = {
            top: { style: 'thin', color: { argb: colors.thinBorder } },
            left: { style: 'thin', color: { argb: colors.thinBorder } },
            bottom: { style: 'thin', color: { argb: colors.thinBorder } },
            right: { style: 'thin', color: { argb: colors.thinBorder } }
        };

        // ==========================================
        // 1. HEADER BANNER WITH IMAGE LOGO
        // ==========================================
        worksheet.getRow(1).height = 25;
        worksheet.getRow(2).height = 25;

        // Fill cells A1 to L2 with the dark maroon color block seamlessly
        for (let row = 1; row <= 2; row++) {
            for (let col = 1; col <= 12; col++) {
                const cell = worksheet.getCell(row, col);
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.mainBanner } };
            }
        }

        // CORRECTED: Merge from A1 instead of B1 to stretch completely across the page width
        worksheet.mergeCells("A1:L2");
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "MEDICAL DEVICE RISK EVALUATION CHECKLIST REPORT";
        titleCell.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FFFFFF" } };
        titleCell.alignment = { horizontal: "center", vertical: "middle" };

        try {
            const logoBase64 = await getBase64FromUrl(reportslogo);
            const imageId = workbook.addImage({
                base64: logoBase64,
                extension: 'png',
            });

            // Adjust scaling positioning coordinates inside cell A1
            worksheet.addImage(imageId, {
                tl: { col: 0.1, row: 0.15 },
                ext: { width: 90, height: 60 }
            });
        } catch (imgError) {
            console.error("Logo image failed to bind to excel sheet output", imgError);
        }

        // ==========================================
        // 2. SUBHEADER & INSTRUCTIONS
        // ==========================================
        worksheet.mergeCells("A3:L3");
        const subCell = worksheet.getCell("A3");
        subCell.value = `${riskNmae} Risk Level`;
        subCell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "1B4F72" } };
        subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.subBanner } };
        subCell.alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getRow(3).height = 24;

        // Executive Summary Section
        worksheet.getCell("A5").value = "Executive Summary";
        worksheet.getCell("A5").font = { name: "Calibri", bold: true, size: 11 };

        worksheet.mergeCells("A6:L7");
        const summaryCell = worksheet.getCell("A6");
        summaryCell.value = "This report summarises the Medical Device Evaluation Checklist conducted for medical devices classified as High Risk under the Medical Device Risk Assessment framework. Each asset is assessed based on six risk criteria including patient impact, safety or performance failure, hazard exposure, repair cost, physical condition, and breakdown frequency. The evaluation helps determine the current condition of high-risk assets and supports monitoring, maintenance intervention, and replacement planning.";
        summaryCell.font = { name: "Calibri", size: 11 };
        summaryCell.alignment = { wrapText: true, vertical: "top" };

        // Asset Condition Rules Explanations
        worksheet.getCell("A9").value = "Asset Condition Classification";
        worksheet.getCell("A9").font = { name: "Calibri", bold: true, size: 11 };

        worksheet.getCell("A10").value = "A. Rule 1: Critical Risk Trigger (Auto Poor Condition)";
        worksheet.getCell("A10").font = { name: "Calibri", bold: true, color: { argb: "1A5276" }, size: 11 };

        worksheet.getCell("A11").value = {
            richText: [
                {
                    text: "If any one of the following critical criteria is selected, the asset will be automatically classified as ",
                    font: { name: "Calibri", size: 11, color: { argb: "000000" } }
                },
                {
                    text: "Poor Condition",
                    font: { name: "Calibri", size: 11, bold: true, color: { argb: "FF0000" } }
                },
                {
                    text: ":",
                    font: { name: "Calibri", size: 11, color: { argb: "000000" } }
                }
            ]
        };
        worksheet.getCell("A12").value = " 1) Patient Impact Risk";
        worksheet.getCell("A13").value = " 2) Hazard Risk";
        worksheet.getCell("A14").value = " 3) Failed Safety or Performance Requirement";

        worksheet.mergeCells("A16:L16");
        worksheet.getCell("A16").value = "These criteria represent direct safety or operational risk to clinical services, therefore the system will automatically label the asset as Poor Condition regardless of other evaluation results.";
        worksheet.getCell("A16").font = { name: "Calibri", size: 11 };

        worksheet.getCell("A18").value = "B. Rule 2: Tick Count Evaluation";
        worksheet.getCell("A18").font = { name: "Calibri", bold: true, color: { argb: "1A5276" }, size: 11 };
        worksheet.getCell("A19").value = "If Rule 1 is not triggered, the asset condition will be determined based on the number of risk criteria selected.";
        worksheet.getCell("A19").font = { name: "Calibri", size: 11 };

        // CORRECTED: Fixed spacing rule name header string to match template precisely
        const ruleTableRows = [
            ["Criteria Selected", "Asset Condition"],
            ["≥ 3 criteria", "Poor"],
            ["< 3 criteria", "Fair"],
            ["0 criteria", "Good"]
        ];

        ruleTableRows.forEach((rowItems, rowIndex) => {
            const targetRowNumber = 21 + rowIndex;
            rowItems.forEach((textValue, colIndex) => {
                const ruleCell = worksheet.getCell(targetRowNumber, colIndex + 1);
                ruleCell.value = textValue;
                ruleCell.font = { name: "Calibri", size: 11 };
                ruleCell.border = thinBorderConfig;
                ruleCell.alignment = { horizontal: "center", vertical: "middle" };

                if (rowIndex === 0) {
                    ruleCell.font = { name: "Calibri", bold: true, size: 11 };
                    ruleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.tickHeader } };
                } else if (rowIndex === 1 && colIndex === 1) {
                    ruleCell.font = { name: "Calibri", bold: true, color: { argb: "FF0000" }, size: 11 };
                }
            });
        });

        // ==========================================
        // 3. MAIN ASSET DATA TABLE
        // ==========================================
        const tableHeaderRow = 27;
        worksheet.getRow(tableHeaderRow).height = 28;

        columnNames.forEach((col, colIdx) => {
            const headerCell = worksheet.getCell(tableHeaderRow, colIdx + 1);
            headerCell.value = col.headerName;
            headerCell.font = { name: "Calibri", bold: true, size: 11, color: { argb: "FFFFFF" } };
            headerCell.border = thinBorderConfig;
            headerCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

            // Apply Teal fill to column index 11 (Asset Condition), Maroon fill to all other headers
            const backgroundHex = (colIdx === 11) ? colors.headerTeal : colors.headerMaroon;
            headerCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: backgroundHex } };
        });

        let dataRowIndex = 28;
        rows.forEach((item, index) => {
            worksheet.getRow(dataRowIndex).height = 22;
            const isZebraRow = (index % 2 === 1);

            const rowValues = [
                item.hospitalName || "",
                item.assetNo || "",
                item.assetDescription || "",
                item.assetAge || 0,
                item.serialNo || "",
                item.departmentDescription || "",
                item.patientimpactrisk || "N",
                item.failedsafetyperformance || "N",
                item.hazardrisk || "N",
                item.highrepaircost || "N",
                item.badphysicalcondition || "N",
                item.breakdownfreq || "N",
                item.assetCondition || "GOOD"
            ];

            rowValues.forEach((cellValue, colIdx) => {
                const cell = worksheet.getCell(dataRowIndex, colIdx + 1);
                cell.value = cellValue;
                cell.font = { name: "Calibri", size: 11, color: { argb: "000000" } };
                cell.border = thinBorderConfig;
                cell.alignment = { vertical: "middle" };

                // Alignments setup
                if (colIdx === 3) {
                    cell.alignment.horizontal = "right"; // Right-align numerical age values
                } else if (colIdx === 2 || colIdx === 4 || colIdx === 5) {
                    cell.alignment.horizontal = "left";  // Left-align descriptive text fields
                } else {
                    cell.alignment.horizontal = "center"; // Center-align status flags and indicators
                }

                // Alternate zebra stripe color layout logic
                if (isZebraRow) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: colors.zebraColor } };
                }

                // Dynamic Status Cells conditional coloring rules
                if (colIdx === 12) {
                    const txt = String(cellValue).toLowerCase().replace(/\s+/g, '');

                    let bgColor = 'FFD1F2EB';   // Default Soft Green
                    let fontColor = 'FF115E59'; // Default Dark Teal

                    if (txt.includes('poor')) {
                        bgColor = 'FFFADBD8';   // Soft Red
                        fontColor = 'FF922B21'; // Dark Red
                    }
                    else if (txt.includes('fair')) {
                        bgColor = 'FFFCF3CF';   // Soft Yellow
                        fontColor = 'FFB7950B'; // Dark Yellow/Gold
                    }

                    cell.font = { name: "Calibri", bold: true, color: { argb: fontColor }, size: 11 };
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
                }
            });
            dataRowIndex++;
        });

        // Set layout column widths
        worksheet.columns = [
            { width: 16 }, // A: Hospital Name
            { width: 16 }, // A: Asset No
            { width: 28 }, // B: Asset Desc
            { width: 12 }, // C: Asset Age
            { width: 18 }, // D: S/N / Model
            { width: 24 }, // E: Dept Desc
            { width: 15 }, // F: Patient Impact
            { width: 16 }, // G: Failed Safety
            { width: 15 }, // H: Hazard Risk
            { width: 15 }, // I: High Repair
            { width: 15 }, // J: Bad Physical
            { width: 16 }, // K: Breakdown Freq
            { width: 18 }  // L: Asset Condition
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const dataBlob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(dataBlob, `${facilityName}_MedicalDeviceRiskChecklist.xlsx`);
    };

    return (
        <DefaultCard sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pb: 2, mb: 3 }}>
                <Box sx={{ flex: '0 0 auto' }}>
                    <img src={reportslogo} alt="IHH Healthcare"
                        style={{ maxWidth: '140px', height: 'auto' }} />
                </Box>

                <Box sx={{ flex: 15, textAlign: 'center' }}>
                    <Typography variant="h5"
                        sx={{ fontWeight: '800', fontSize: '30px', color: '#1a365d', letterSpacing: 0.5 }}>
                        MEDICAL DEVICE RISK EVALUATION CHECKLIST REPORT
                    </Typography>
                    <Typography variant="subtitle1"
                        sx={{ fontWeight: 'bold', fontSize: '20px', color: '#c0392b' }}>
                        {riskNmae} Risk Level
                    </Typography>
                </Box>

                {/* <Box sx={{ flex: 1, textAlign: 'right' }}>
                    <Tooltip title="Export to Excel" placement="bottom" arrow>
                        <IconButton variant="contained" sx={{ left: '-50px' }} onClick={handleExportExcel}>
                            <SaveAltRoundedIcon sx={{ fontSize: '30px' }} />
                        </IconButton>
                    </Tooltip>
                </Box> */}
                <Box className="action-buttons-container" sx={{ flex: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Tooltip title="Export to PDF" arrow>
                        <IconButton color="error" sx={{ left: '-50px' }} onClick={() => pdfReportRef.current.generatePDF()}>
                            <PictureAsPdfRoundedIcon sx={{ fontSize: '32px' }} />
                        </IconButton>
                    </Tooltip>


                    <Tooltip title="Export to Excel" arrow>
                        <IconButton color="success" sx={{ left: '-50px' }} onClick={handleExportExcel}>
                            <DescriptionRoundedIcon sx={{ fontSize: '32px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <ViewAssetMedicalRiskChecklist ref={pdfReportRef} data={data} rows={rows || []} />

            <CustomAgGrid
                refSet={gridApi}
                setGridApi={setGridApi}
                rows={rows}
                columns={columnNames}
                isLoading={loading}

            />
        </DefaultCard>
    );
});

export default AssetMedicalRiskChecklist;